import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, User, Plus, Search, Edit, Trash2, Eye, ChevronRight, ChevronDown, GitBranch, List, Network } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { 
  TableContainer, Table, TableHeader, TableBody, TableRow, 
  TableHead, TableCell, TableEmpty, TableLoading 
} from '../../components/ui/Table';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatDocument, formatDate } from '../../lib/utils';

type StakeholderType = 'OWNER' | 'EMPLOYEE' | 'CUSTOMER' | 'SUPPLIER' | 'PARTNER';

interface Entity {
  id: string;
  tenantId: string;
  type: 'COMPANY' | 'INDIVIDUAL';
  name: string;
  document: string;
  tradeName?: string;
  email: string;
  phone?: string;
  addressStreet?: string;
  addressNumber?: string;
  addressComplement?: string;
  addressNeighborhood?: string;
  addressCity?: string;
  addressState?: string;
  addressPostalCode?: string;
  kycStatus: string;
  status: 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
  createdAt: string;
  updatedAt: string;
  accountsCount?: number;
  // Hierarchy fields
  parentEntityId?: string | null;
  stakeholderType?: StakeholderType;
  parentEntity?: Entity | null;
  childEntities?: Entity[];
  _count?: { childEntities?: number };
}

interface EntityAccount {
  id: string;
  name: string;
  type: string;
  status: string;
  balance: string;
}

const entityTypeOptions = [
  { value: '', label: 'Todos os tipos' },
  { value: 'COMPANY', label: 'Empresa (PJ)' },
  { value: 'INDIVIDUAL', label: 'Pessoa Física (PF)' },
];

const stakeholderTypeOptions = [
  { value: '', label: 'Todos os stakeholders' },
  { value: 'OWNER', label: 'Proprietário/Sócio' },
  { value: 'EMPLOYEE', label: 'Funcionário' },
  { value: 'CUSTOMER', label: 'Cliente' },
  { value: 'SUPPLIER', label: 'Fornecedor' },
  { value: 'PARTNER', label: 'Parceiro' },
];

const stakeholderTypeLabels: Record<StakeholderType, string> = {
  OWNER: 'Proprietário',
  EMPLOYEE: 'Funcionário',
  CUSTOMER: 'Cliente',
  SUPPLIER: 'Fornecedor',
  PARTNER: 'Parceiro',
};

const stakeholderTypeBadgeVariant: Record<StakeholderType, 'success' | 'warning' | 'error' | 'info' | 'primary'> = {
  OWNER: 'primary',
  EMPLOYEE: 'info',
  CUSTOMER: 'success',
  SUPPLIER: 'warning',
  PARTNER: 'primary',
};

const statusOptions = [
  { value: '', label: 'Todos os status' },
  { value: 'ACTIVE', label: 'Ativo' },
  { value: 'INACTIVE', label: 'Inativo' },
  { value: 'BLOCKED', label: 'Bloqueado' },
];

const statusBadgeVariant = {
  ACTIVE: 'success' as const,
  INACTIVE: 'warning' as const,
  BLOCKED: 'error' as const,
};

const kycStatusBadgeVariant = {
  PENDING: 'warning' as const,
  APPROVED: 'success' as const,
  REJECTED: 'error' as const,
  NOT_REQUIRED: 'info' as const,
};

export function AdminEntities() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stakeholderFilter, setStakeholderFilter] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('list');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [entityAccounts, setEntityAccounts] = useState<EntityAccount[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    type: 'COMPANY' as 'COMPANY' | 'INDIVIDUAL',
    name: '',
    document: '',
    tradeName: '',
    email: '',
    phone: '',
    addressStreet: '',
    addressNumber: '',
    addressComplement: '',
    addressNeighborhood: '',
    addressCity: '',
    addressState: '',
    addressPostalCode: '',
    parentEntityId: '' as string,
    stakeholderType: 'OWNER' as StakeholderType,
  });

  // Fetch entities
  const { data: entities = [], isLoading } = useQuery({
    queryKey: ['entities', typeFilter, statusFilter, stakeholderFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (typeFilter) params.append('type', typeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (stakeholderFilter) params.append('stakeholderType', stakeholderFilter);
      const url = `${ENDPOINTS.entities.list}${params.toString() ? `?${params}` : ''}`;
      const response = await api.get(url);
      return response.data;
    },
  });

  // Fetch entity tree for tree view
  const { data: entityTree = [] } = useQuery({
    queryKey: ['entities', 'tree'],
    queryFn: async () => {
      const response = await api.get('/entities/tree');
      return response.data;
    },
    enabled: viewMode === 'tree',
  });

  // Build tree structure from flat list for tree view
  const buildTreeFromEntities = useMemo(() => {
    if (viewMode !== 'tree') return [];
    
    // If we have tree data from API, use it
    if (entityTree.length > 0) return entityTree;
    
    // Otherwise build from flat list
    const entityMap = new Map<string, Entity & { children: Entity[] }>();
    const roots: (Entity & { children: Entity[] })[] = [];
    
    // First pass: create map with children arrays
    entities.forEach((entity: Entity) => {
      entityMap.set(entity.id, { ...entity, children: [] });
    });
    
    // Second pass: build tree
    entities.forEach((entity: Entity) => {
      const node = entityMap.get(entity.id)!;
      if (entity.parentEntityId && entityMap.has(entity.parentEntityId)) {
        entityMap.get(entity.parentEntityId)!.children.push(node);
      } else {
        roots.push(node);
      }
    });
    
    return roots;
  }, [entities, entityTree, viewMode]);

  // Get valid parent options (exclude self and descendants)
  const getValidParentOptions = useMemo(() => {
    const options = [{ value: '', label: 'Nenhum (Entidade Raiz)' }];
    
    const getDescendantIds = (entityId: string): Set<string> => {
      const descendants = new Set<string>();
      const findDescendants = (id: string) => {
        entities.forEach((e: Entity) => {
          if (e.parentEntityId === id) {
            descendants.add(e.id);
            findDescendants(e.id);
          }
        });
      };
      findDescendants(entityId);
      return descendants;
    };
    
    const excludeIds = selectedEntity 
      ? new Set([selectedEntity.id, ...getDescendantIds(selectedEntity.id)])
      : new Set<string>();
    
    entities
      .filter((e: Entity) => e.status === 'ACTIVE' && !excludeIds.has(e.id))
      .forEach((e: Entity) => {
        options.push({ 
          value: e.id, 
          label: `${e.name} (${e.type === 'COMPANY' ? 'PJ' : 'PF'})` 
        });
      });
    
    return options;
  }, [entities, selectedEntity]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: Omit<typeof formData, 'parentEntityId'> & { parentEntityId?: string }) => 
      api.post(ENDPOINTS.entities.create, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
      setIsCreateModalOpen(false);
      resetForm();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Omit<typeof formData, 'parentEntityId'>> & { parentEntityId?: string } }) =>
      api.put(ENDPOINTS.entities.update(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
      setIsEditModalOpen(false);
      setSelectedEntity(null);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(ENDPOINTS.entities.delete(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
    },
  });

  const resetForm = () => {
    setFormData({
      type: 'COMPANY',
      name: '',
      document: '',
      tradeName: '',
      email: '',
      phone: '',
      addressStreet: '',
      addressNumber: '',
      addressComplement: '',
      addressNeighborhood: '',
      addressCity: '',
      addressState: '',
      addressPostalCode: '',
      parentEntityId: '',
      stakeholderType: 'OWNER',
    });
  };

  const handleCreate = () => {
    const payload = {
      ...formData,
      parentEntityId: formData.parentEntityId || undefined,
    };
    createMutation.mutate(payload);
  };

  const handleEdit = (entity: Entity) => {
    setSelectedEntity(entity);
    setFormData({
      type: entity.type,
      name: entity.name,
      document: entity.document,
      tradeName: entity.tradeName || '',
      email: entity.email,
      phone: entity.phone || '',
      addressStreet: entity.addressStreet || '',
      addressNumber: entity.addressNumber || '',
      addressComplement: entity.addressComplement || '',
      addressNeighborhood: entity.addressNeighborhood || '',
      addressCity: entity.addressCity || '',
      addressState: entity.addressState || '',
      addressPostalCode: entity.addressPostalCode || '',
      parentEntityId: entity.parentEntityId || '',
      stakeholderType: entity.stakeholderType || 'OWNER',
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = () => {
    if (!selectedEntity) return;
    const { type, document, ...updateData } = formData;
    const payload = {
      ...updateData,
      parentEntityId: updateData.parentEntityId || undefined,
    };
    updateMutation.mutate({ id: selectedEntity.id, data: payload });
  };

  const toggleNode = (entityId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(entityId)) {
        next.delete(entityId);
      } else {
        next.add(entityId);
      }
      return next;
    });
  };

  const handleView = async (entity: Entity) => {
    setSelectedEntity(entity);
    try {
      const response = await api.get(ENDPOINTS.entities.accounts(entity.id));
      setEntityAccounts(response.data.accounts || []);
    } catch {
      setEntityAccounts([]);
    }
    setIsViewModalOpen(true);
  };

  const handleDelete = (entity: Entity) => {
    if (confirm(`Deseja desativar a entidade "${entity.name}"? Todas as contas serão bloqueadas.`)) {
      deleteMutation.mutate(entity.id);
    }
  };

  // Filter entities by search
  const filteredEntities = entities.filter((entity: Entity) => {
    const searchLower = search.toLowerCase();
    return (
      entity.name.toLowerCase().includes(searchLower) ||
      entity.document.includes(search) ||
      entity.email.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Entidades</h1>
          <p className="text-surface-400">Gerencie empresas e pessoas físicas do tenant</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Entidade
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Buscar por nome, documento ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <Select
            options={entityTypeOptions}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-40"
          />
          <Select
            options={stakeholderTypeOptions}
            value={stakeholderFilter}
            onChange={(e) => setStakeholderFilter(e.target.value)}
            className="w-48"
          />
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-40"
          />
          <div className="flex gap-1 border border-surface-700 rounded-lg p-1">
            <Button
              variant={viewMode === 'list' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              title="Visualização em Lista"
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'tree' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('tree')}
              title="Visualização em Árvore"
            >
              <Network className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Table / Tree View */}
      <Card>
        {viewMode === 'list' ? (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>Stakeholder</TableHead>
                  <TableHead>Hierarquia</TableHead>
                  <TableHead>Contas</TableHead>
                  <TableHead>KYC</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableLoading columns={9} rows={5} />
                ) : filteredEntities.length === 0 ? (
                  <TableEmpty title="Nenhuma entidade encontrada" />
                ) : (
                  filteredEntities.map((entity: Entity) => (
                    <TableRow key={entity.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {entity.type === 'COMPANY' ? (
                            <Building2 className="w-4 h-4 text-primary-400" />
                          ) : (
                            <User className="w-4 h-4 text-accent-400" />
                          )}
                          <span>{entity.type === 'COMPANY' ? 'PJ' : 'PF'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{entity.name}</div>
                          {entity.tradeName && (
                            <div className="text-xs text-surface-400">{entity.tradeName}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatDocument(entity.document)}</TableCell>
                      <TableCell>
                        {entity.stakeholderType && (
                          <Badge variant={stakeholderTypeBadgeVariant[entity.stakeholderType]}>
                            {stakeholderTypeLabels[entity.stakeholderType]}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {entity.parentEntityId ? (
                            <div className="flex items-center gap-1 text-xs text-surface-400">
                              <GitBranch className="w-3 h-3" />
                              <span>Filho</span>
                            </div>
                          ) : (
                            <span className="text-xs text-surface-500">Raiz</span>
                          )}
                          {(entity._count?.childEntities || 0) > 0 && (
                            <Badge variant="info" className="ml-1 text-xs">
                              {entity._count?.childEntities} filhos
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="info">{entity.accountsCount || 0}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={kycStatusBadgeVariant[entity.kycStatus as keyof typeof kycStatusBadgeVariant] || 'info'}>
                          {entity.kycStatus}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusBadgeVariant[entity.status]}>
                          {entity.status === 'ACTIVE' ? 'Ativo' : entity.status === 'INACTIVE' ? 'Inativo' : 'Bloqueado'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleView(entity)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(entity)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          {entity.status === 'ACTIVE' && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(entity)}>
                              <Trash2 className="w-4 h-4 text-error" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <div className="p-4">
            <EntityTreeView
              entities={buildTreeFromEntities}
              expandedNodes={expandedNodes}
              onToggle={toggleNode}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              level={0}
            />
            {buildTreeFromEntities.length === 0 && !isLoading && (
              <div className="text-center py-8 text-surface-400">
                Nenhuma entidade encontrada
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Nova Entidade"
        size="lg"
      >
        <EntityForm
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleCreate}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={createMutation.isPending}
          isEdit={false}
          parentOptions={getValidParentOptions}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Editar Entidade"
        size="lg"
      >
        <EntityForm
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleUpdate}
          onCancel={() => setIsEditModalOpen(false)}
          isLoading={updateMutation.isPending}
          isEdit={true}
          parentOptions={getValidParentOptions}
        />
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Detalhes da Entidade"
        size="lg"
      >
        {selectedEntity && (
          <EntityDetails entity={selectedEntity} accounts={entityAccounts} />
        )}
      </Modal>
    </div>
  );
}

// Default export for compatibility
export default AdminEntities;


// Entity Form Component
interface EntityFormProps {
  formData: {
    type: 'COMPANY' | 'INDIVIDUAL';
    name: string;
    document: string;
    tradeName: string;
    email: string;
    phone: string;
    addressStreet: string;
    addressNumber: string;
    addressComplement: string;
    addressNeighborhood: string;
    addressCity: string;
    addressState: string;
    addressPostalCode: string;
    parentEntityId: string;
    stakeholderType: StakeholderType;
  };
  setFormData: (data: EntityFormProps['formData'] | ((prev: EntityFormProps['formData']) => EntityFormProps['formData'])) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading: boolean;
  isEdit: boolean;
  parentOptions: { value: string; label: string }[];
}

function EntityForm({ formData, setFormData, onSubmit, onCancel, isLoading, isEdit, parentOptions }: EntityFormProps) {
  const handleChange = (field: string, value: string) => {
    setFormData((prev: EntityFormProps['formData']) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Tipo"
          options={[
            { value: 'COMPANY', label: 'Empresa (PJ)' },
            { value: 'INDIVIDUAL', label: 'Pessoa Física (PF)' },
          ]}
          value={formData.type}
          onChange={(e) => handleChange('type', e.target.value)}
          disabled={isEdit}
        />
        <Input
          label={formData.type === 'COMPANY' ? 'CNPJ' : 'CPF'}
          placeholder={formData.type === 'COMPANY' ? '00000000000000' : '00000000000'}
          value={formData.document}
          onChange={(e) => handleChange('document', e.target.value.replace(/\D/g, ''))}
          maxLength={formData.type === 'COMPANY' ? 14 : 11}
          disabled={isEdit}
        />
      </div>

      <Input
        label={formData.type === 'COMPANY' ? 'Razão Social' : 'Nome Completo'}
        placeholder="Nome da entidade"
        value={formData.name}
        onChange={(e) => handleChange('name', e.target.value)}
      />

      {formData.type === 'COMPANY' && (
        <Input
          label="Nome Fantasia"
          placeholder="Nome fantasia (opcional)"
          value={formData.tradeName}
          onChange={(e) => handleChange('tradeName', e.target.value)}
        />
      )}

      {/* Hierarchy Fields */}
      <div className="border-t border-surface-700 pt-4 mt-4">
        <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-primary-400" />
          Hierarquia e Relacionamento
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Entidade Pai"
            options={parentOptions}
            value={formData.parentEntityId}
            onChange={(e) => handleChange('parentEntityId', e.target.value)}
          />
          <Select
            label="Tipo de Stakeholder"
            options={[
              { value: 'OWNER', label: 'Proprietário/Sócio' },
              { value: 'EMPLOYEE', label: 'Funcionário' },
              { value: 'CUSTOMER', label: 'Cliente' },
              { value: 'SUPPLIER', label: 'Fornecedor' },
              { value: 'PARTNER', label: 'Parceiro' },
            ]}
            value={formData.stakeholderType}
            onChange={(e) => handleChange('stakeholderType', e.target.value)}
          />
        </div>
        <p className="text-xs text-surface-400 mt-2">
          A entidade pai define a hierarquia organizacional. O tipo de stakeholder indica o relacionamento com o tenant.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Email"
          type="email"
          placeholder="email@exemplo.com"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
        />
        <Input
          label="Telefone"
          placeholder="11999999999"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, ''))}
        />
      </div>

      <div className="border-t border-surface-700 pt-4 mt-4">
        <h4 className="text-sm font-medium mb-3">Endereço (opcional)</h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <Input
              label="Logradouro"
              placeholder="Rua, Avenida..."
              value={formData.addressStreet}
              onChange={(e) => handleChange('addressStreet', e.target.value)}
            />
          </div>
          <Input
            label="Número"
            placeholder="123"
            value={formData.addressNumber}
            onChange={(e) => handleChange('addressNumber', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <Input
            label="Complemento"
            placeholder="Sala, Apto..."
            value={formData.addressComplement}
            onChange={(e) => handleChange('addressComplement', e.target.value)}
          />
          <Input
            label="Bairro"
            placeholder="Bairro"
            value={formData.addressNeighborhood}
            onChange={(e) => handleChange('addressNeighborhood', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-3 gap-4 mt-3">
          <Input
            label="Cidade"
            placeholder="Cidade"
            value={formData.addressCity}
            onChange={(e) => handleChange('addressCity', e.target.value)}
          />
          <Input
            label="Estado"
            placeholder="UF"
            maxLength={2}
            value={formData.addressState}
            onChange={(e) => handleChange('addressState', e.target.value.toUpperCase())}
          />
          <Input
            label="CEP"
            placeholder="00000000"
            maxLength={8}
            value={formData.addressPostalCode}
            onChange={(e) => handleChange('addressPostalCode', e.target.value.replace(/\D/g, ''))}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-surface-700">
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button onClick={onSubmit} disabled={isLoading}>
          {isLoading ? 'Salvando...' : isEdit ? 'Atualizar' : 'Criar'}
        </Button>
      </div>
    </div>
  );
}

// Entity Details Component
interface EntityDetailsProps {
  entity: Entity;
  accounts: EntityAccount[];
}

function EntityDetails({ entity, accounts }: EntityDetailsProps) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-surface-400">Tipo</label>
          <p className="font-medium flex items-center gap-2">
            {entity.type === 'COMPANY' ? (
              <>
                <Building2 className="w-4 h-4 text-primary-400" />
                Empresa (PJ)
              </>
            ) : (
              <>
                <User className="w-4 h-4 text-accent-400" />
                Pessoa Física (PF)
              </>
            )}
          </p>
        </div>
        <div>
          <label className="text-xs text-surface-400">Status</label>
          <p>
            <Badge variant={statusBadgeVariant[entity.status]}>
              {entity.status === 'ACTIVE' ? 'Ativo' : entity.status === 'INACTIVE' ? 'Inativo' : 'Bloqueado'}
            </Badge>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-surface-400">Nome</label>
          <p className="font-medium">{entity.name}</p>
          {entity.tradeName && (
            <p className="text-sm text-surface-400">{entity.tradeName}</p>
          )}
        </div>
        <div>
          <label className="text-xs text-surface-400">Documento</label>
          <p className="font-medium">{formatDocument(entity.document)}</p>
        </div>
      </div>

      {/* Hierarchy Info */}
      <div className="border-t border-surface-700 pt-4">
        <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-primary-400" />
          Hierarquia e Relacionamento
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-surface-400">Tipo de Stakeholder</label>
            <p>
              {entity.stakeholderType && (
                <Badge variant={stakeholderTypeBadgeVariant[entity.stakeholderType]}>
                  {stakeholderTypeLabels[entity.stakeholderType]}
                </Badge>
              )}
            </p>
          </div>
          <div>
            <label className="text-xs text-surface-400">Entidade Pai</label>
            <p className="font-medium">
              {entity.parentEntity ? (
                <span className="flex items-center gap-1">
                  <GitBranch className="w-3 h-3 text-surface-400" />
                  {entity.parentEntity.name}
                </span>
              ) : (
                <span className="text-surface-500">Nenhuma (Raiz)</span>
              )}
            </p>
          </div>
        </div>
        {(entity._count?.childEntities || 0) > 0 && (
          <div className="mt-2">
            <label className="text-xs text-surface-400">Entidades Filhas</label>
            <p>
              <Badge variant="info">{entity._count?.childEntities} entidades</Badge>
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-surface-400">Email</label>
          <p>{entity.email}</p>
        </div>
        <div>
          <label className="text-xs text-surface-400">Telefone</label>
          <p>{entity.phone || '-'}</p>
        </div>
      </div>

      {entity.addressStreet && (
        <div>
          <label className="text-xs text-surface-400">Endereço</label>
          <p>
            {entity.addressStreet}, {entity.addressNumber}
            {entity.addressComplement && ` - ${entity.addressComplement}`}
          </p>
          <p className="text-sm text-surface-400">
            {entity.addressNeighborhood} - {entity.addressCity}/{entity.addressState} - CEP {entity.addressPostalCode}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-surface-400">KYC Status</label>
          <p>
            <Badge variant={kycStatusBadgeVariant[entity.kycStatus as keyof typeof kycStatusBadgeVariant] || 'info'}>
              {entity.kycStatus}
            </Badge>
          </p>
        </div>
        <div>
          <label className="text-xs text-surface-400">Criado em</label>
          <p>{formatDate(entity.createdAt)}</p>
        </div>
      </div>

      {/* Accounts */}
      <div className="border-t border-surface-700 pt-4">
        <h4 className="font-medium mb-3">Contas Vinculadas ({accounts.length})</h4>
        {accounts.length === 0 ? (
          <p className="text-surface-400 text-sm">Nenhuma conta vinculada</p>
        ) : (
          <div className="space-y-2">
            {accounts.map((account) => (
              <div
                key={account.id}
                className="flex items-center justify-between p-3 bg-surface-800 rounded-lg"
              >
                <div>
                  <p className="font-medium">{account.name}</p>
                  <p className="text-xs text-surface-400">{account.type}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium">R$ {parseFloat(account.balance).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  <Badge variant={account.status === 'ACTIVE' ? 'success' : 'warning'} className="text-xs">
                    {account.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Entity Tree View Component
interface EntityTreeNode extends Entity {
  children?: EntityTreeNode[];
}

interface EntityTreeViewProps {
  entities: EntityTreeNode[];
  expandedNodes: Set<string>;
  onToggle: (id: string) => void;
  onView: (entity: Entity) => void;
  onEdit: (entity: Entity) => void;
  onDelete: (entity: Entity) => void;
  level: number;
}

function EntityTreeView({ entities, expandedNodes, onToggle, onView, onEdit, onDelete, level }: EntityTreeViewProps) {
  return (
    <div className={level > 0 ? 'ml-6 border-l border-surface-700 pl-4' : ''}>
      {entities.map((entity) => {
        const hasChildren = entity.children && entity.children.length > 0;
        const isExpanded = expandedNodes.has(entity.id);
        
        return (
          <div key={entity.id} className="mb-2">
            <div className="flex items-center gap-2 p-3 bg-surface-800 rounded-lg hover:bg-surface-750 transition-colors">
              {/* Expand/Collapse Button */}
              <button
                onClick={() => hasChildren && onToggle(entity.id)}
                className={`w-6 h-6 flex items-center justify-center rounded ${hasChildren ? 'hover:bg-surface-700 cursor-pointer' : 'opacity-30 cursor-default'}`}
                disabled={!hasChildren}
              >
                {hasChildren ? (
                  isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )
                ) : (
                  <div className="w-2 h-2 rounded-full bg-surface-600" />
                )}
              </button>
              
              {/* Entity Icon */}
              {entity.type === 'COMPANY' ? (
                <Building2 className="w-5 h-5 text-primary-400" />
              ) : (
                <User className="w-5 h-5 text-accent-400" />
              )}
              
              {/* Entity Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{entity.name}</span>
                  {entity.stakeholderType && (
                    <Badge variant={stakeholderTypeBadgeVariant[entity.stakeholderType]} className="text-xs">
                      {stakeholderTypeLabels[entity.stakeholderType]}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-surface-400">
                  <span>{formatDocument(entity.document)}</span>
                  <span>•</span>
                  <span>{entity.email}</span>
                  {hasChildren && (
                    <>
                      <span>•</span>
                      <span className="text-primary-400">{entity.children?.length} filhos</span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Status */}
              <Badge variant={statusBadgeVariant[entity.status]} className="text-xs">
                {entity.status === 'ACTIVE' ? 'Ativo' : entity.status === 'INACTIVE' ? 'Inativo' : 'Bloqueado'}
              </Badge>
              
              {/* Actions */}
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => onView(entity)}>
                  <Eye className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onEdit(entity)}>
                  <Edit className="w-4 h-4" />
                </Button>
                {entity.status === 'ACTIVE' && (
                  <Button variant="ghost" size="sm" onClick={() => onDelete(entity)}>
                    <Trash2 className="w-4 h-4 text-error" />
                  </Button>
                )}
              </div>
            </div>
            
            {/* Children */}
            {hasChildren && isExpanded && (
              <EntityTreeView
                entities={entity.children!}
                expandedNodes={expandedNodes}
                onToggle={onToggle}
                onView={onView}
                onEdit={onEdit}
                onDelete={onDelete}
                level={level + 1}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
